import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, KeyRound } from "lucide-react";

export const isCodeReady = (c) => /^\d{6}$/.test(c) || (c || "").replace(/[^A-Za-z0-9]/g, "").length === 10;

// 6-digit authenticator input with a toggle to type a recovery code instead.
export function CodeInput({ value, onChange, testId, allowRecovery = true }) {
  const [recovery, setRecovery] = useState(false);
  const toggle = () => { setRecovery(!recovery); onChange(""); };
  return (
    <div>
      {recovery ? (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 11))}
          placeholder="XXXXX-XXXXX"
          data-testid={`${testId}-recovery`}
          className="mt-2 w-full border border-black/20 px-3 py-3 text-center font-mono text-xl tracking-[0.3em] outline-none focus:border-[#002FA7]"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          data-testid={testId}
          className="mt-2 w-full border border-black/20 px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] outline-none focus:border-[#002FA7]"
        />
      )}
      {allowRecovery ? (
        <button type="button" onClick={toggle} data-testid={`${testId}-toggle-recovery`} className="mt-1.5 text-[11px] text-[#002FA7] underline underline-offset-2">
          {recovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
        </button>
      ) : null}
    </div>
  );
}

export function RecoveryCodesList({ codes, onDone }) {
  const text = codes.join("\n");
  function download() {
    const url = URL.createObjectURL(new Blob([`VOLMEE recovery codes\nEach code works once.\n\n${text}\n`], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url; a.download = "volmee-recovery-codes.txt";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }
  return (
    <div className="border-2 border-[#FFC300] bg-[#FFF9E5] p-4" data-testid="recovery-codes-panel">
      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#5A4200]"><KeyRound className="h-4 w-4" /> Save your recovery codes</div>
      <p className="mt-1 text-xs text-[#5A4200]">If you lose your phone, each of these codes works <b>once</b> instead of the 6-digit code. They won't be shown again.</p>
      <div className="mt-3 grid grid-cols-2 gap-1.5 font-mono text-sm font-semibold" data-testid="recovery-codes-list">
        {codes.map((c) => <div key={c} className="border border-black/10 bg-white px-2 py-1 text-center">{c}</div>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Codes copied"))} data-testid="recovery-codes-copy-btn" className="inline-flex items-center gap-1 border border-black/20 bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white"><Copy className="h-3 w-3" /> Copy</button>
        <button type="button" onClick={download} data-testid="recovery-codes-download-btn" className="inline-flex items-center gap-1 border border-black/20 bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white"><Download className="h-3 w-3" /> Download .txt</button>
        {onDone ? <button type="button" onClick={onDone} data-testid="recovery-codes-done-btn" className="ml-auto border border-black bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-zinc-800">I've saved them</button> : null}
      </div>
    </div>
  );
}

// QR + manual key + confirm. `setup` is the {qr_data_url, shared_secret} payload already fetched.
export function MfaSetupConfirm({ setup, onEnabled }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  async function confirm() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/confirm", { code });
      toast.success("Authenticator set up");
      onEnabled(data.recovery_codes || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-3" data-testid="mfa-setup-confirm-block">
      <img src={setup.qr_data_url} alt="Authenticator QR code" className="mx-auto h-40 w-40 border border-black/10 bg-white" data-testid="mfa-setup-qr" />
      <div className="mt-2 text-center text-[11px] text-zinc-600">Can't scan? Enter this key manually:</div>
      <div className="mt-1 break-all text-center font-mono text-xs font-semibold" data-testid="mfa-setup-secret">{setup.shared_secret}</div>
      <CodeInput value={code} onChange={setCode} testId="mfa-setup-code-input" allowRecovery={false} />
      <button type="button" onClick={confirm} disabled={busy || code.length !== 6} data-testid="mfa-setup-confirm-btn" className="mt-3 w-full border border-black bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-40">
        {busy ? "Checking…" : "Confirm authenticator"}
      </button>
    </div>
  );
}
